import React from "react";
import { format, Locale } from "date-fns";
import { MdOutlineKeyboardArrowLeft, MdOutlineKeyboardArrowRight, MdChevronRight } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Exercise } from "../../../../../../models/Exercise";
import { Ticket } from "../../../../../../models/Ticket";
import { SwimmingLesson } from "../../../../../../models/SwimmingScheduleData";
import EAIconButton from "../../../../../shared/Buttons/EAIconButton";
import EAAlert from "../../../../../shared/EAAlert";
import EASkeletonLoader from "../../../../../shared/EASkeletonLoader";
import useLocaleByLanguage from "../../../../../../../utils/hooks/useLocaleByLanguage";
import useChangeSwimmingTermSlots from "./useChangeSwimmingTermSlots";
import { formatSwimmingLessonTime } from "./formatSwimmingTermLabel";
import { pluralMiejsc } from "../../../../../shared/TimeSlot/utils";
import "./ChangeSwimmingTerm.scss";

interface IProps {
  exercise: Exercise;
  ticket: Ticket;
  enabled: boolean;
  onSelectLesson: (lesson: SwimmingLesson) => void;
}

interface DayGroup {
  key: string;
  dayLabel: string;
  lessons: SwimmingLesson[];
}

const groupByDay = (lessons: SwimmingLesson[], locale: Locale): DayGroup[] => {
  const groups: DayGroup[] = [];
  lessons.forEach((lesson) => {
    const date = new Date(lesson.startTime < 1e11 ? lesson.startTime * 1000 : lesson.startTime);
    const key = format(date, "yyyy-MM-dd");
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.lessons.push(lesson);
      return;
    }
    groups.push({ key, dayLabel: format(date, "EEEE, dd MMMM", { locale }), lessons: [lesson] });
  });
  // W obrębie dnia grupujemy dodatkowo po name (np. "Kort 1"/"Kort 2") — bez tego dwa
  // równoległe terminy o tej samej godzinie na różnych kortach są nie do odróżnienia
  // przy szybkim przewijaniu, bo lecą przeplecione czysto chronologicznie.
  groups.forEach((group) => {
    group.lessons.sort((a, b) => {
      const nameCompare = (a.name || "").localeCompare(b.name || "", "pl");
      if (nameCompare !== 0) return nameCompare;
      return a.startTime - b.startTime;
    });
  });
  return groups;
};

const ChangeSwimmingTermSlots: React.FC<IProps> = ({ exercise, ticket, enabled, onSelectLesson }) => {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const {
    slots,
    loading,
    error,
    dateFrom,
    dateTo,
    canGoPrevious,
    canGoNext,
    handlePreviousWeek,
    handleNextWeek,
  } = useChangeSwimmingTermSlots({ exercise, ticket, enabled });

  const weekLabel = `${format(dateFrom, "dd.MM")} – ${format(dateTo, "dd.MM.yyyy")}`;
  const dayGroups = groupByDay(slots, locale);

  return (
    <div className="change-swimming-term-slots">
      <div className="change-swimming-term-slots__nav">
        <EAIconButton
          title={t("poprzedni_tydzien")}
          icon={<MdOutlineKeyboardArrowLeft />}
          variant="outlined"
          onClick={handlePreviousWeek}
          disabled={!canGoPrevious}
        />
        <span className="body-text-small text-color-dark">{weekLabel}</span>
        <EAIconButton
          title={t("nastepny_tydzien")}
          icon={<MdOutlineKeyboardArrowRight />}
          variant="outlined"
          onClick={handleNextWeek}
          disabled={!canGoNext}
        />
      </div>

      {loading && (
        <div className="change-swimming-term-slots__loading">
          <EASkeletonLoader height="72px" />
          <EASkeletonLoader height="72px" />
          <EASkeletonLoader height="72px" />
        </div>
      )}

      {!loading && error && <EAAlert type="error" message={t("nie_udalo_sie_pobrac_terminow")} />}

      {!loading && !error && slots.length === 0 && (
        <p className="body-text-small text-color-light-gray">{t("brak_wolnych_terminow_w_tym_tygodniu")}</p>
      )}

      {!loading && !error && dayGroups.length > 0 && (
        <div className="change-swimming-term-slots__groups">
          {dayGroups.map((group) => (
            <div key={group.key} className="change-swimming-term-slots__group">
              <span className="change-swimming-term-slots__day-label">{group.dayLabel}</span>
              <div className="change-swimming-term-slots__list">
                {group.lessons.map((lesson) => {
                  const taken = lesson.reserved + (lesson.prereservation ?? 0);
                  const remaining = Math.max(0, lesson.capacity - taken);
                  return (
                    <div
                      key={lesson.objectId}
                      className="change-swimming-term-slots__item"
                      onClick={() => onSelectLesson(lesson)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onSelectLesson(lesson);
                        }
                      }}
                    >
                      <span className="change-swimming-term-slots__item-time">
                        {formatSwimmingLessonTime(lesson)}
                      </span>
                      {lesson.name && (
                        <span className="change-swimming-term-slots__item-name text-color-dark-gray">
                          {lesson.name}
                        </span>
                      )}
                      {lesson.location && lesson.location !== lesson.name && (
                        <span className="change-swimming-term-slots__item-location text-color-light-gray">
                          {lesson.location}
                        </span>
                      )}
                      <span className="change-swimming-term-slots__item-spots text-color-light-gray">
                        {remaining} {pluralMiejsc(remaining)}
                      </span>
                      <MdChevronRight className="change-swimming-term-slots__item-chevron" />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChangeSwimmingTermSlots;
